import { MoreHorizontal, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { getBottomTabLinks, getMoreLinks } from "./nav-links";
import useCurrentUser from "@/store/useCurrentUser";
import type { UserRole } from "@/types/user";
import type { NavLink } from "./nav-item";

// ─── Bottom tab bar (mobile only, < md) ──────────────────────────────────────────
function BottomTabBar() {
  const user = useCurrentUser((state: any) => state.user);
  const role = user?.role as UserRole | undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const activePath = location.pathname;

  const tabLinks = getBottomTabLinks(role);
  const moreLinks = getMoreLinks(role);

  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const isTabActive = (href?: string) =>
    href === activePath || (href && activePath.startsWith(href + "/"));

  const handleTabClick = (href: string) => {
    navigate(href);
  };

  return (
    <>
      {/* ── Tab bar ────────────────────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-14 bg-(--color-bg-surface) border-t border-(--color-border-default) flex items-stretch z-30">
        {tabLinks.map((tab) => {
          const active = isTabActive(tab.href);
          return (
            <button
              key={tab.label}
              onClick={() => tab.href && handleTabClick(tab.href)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                active
                  ? "text-[var(--color-info-600)]"
                  : "text-[var(--color-text-tertiary)]"
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-[11px] font-medium">{tab.label}</span>
              {active && (
                <span className="absolute bottom-0 h-0.5 w-8 rounded-full bg-[var(--color-info-500)]" />
              )}
            </button>
          );
        })}

        {/* "More" tab */}
        {moreLinks.length > 0 && (
          <button
            onClick={() => setIsMoreOpen(true)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${
              isMoreOpen
                ? "text-[var(--color-info-600)]"
                : "text-[var(--color-text-tertiary)]"
            }`}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[11px] font-medium">More</span>
          </button>
        )}
      </nav>

      {/* ── "More" bottom sheet ────────────────────────────────── */}
      {isMoreOpen && (
        <MoreBottomSheet
          links={moreLinks}
          onClose={() => setIsMoreOpen(false)}
          onNavigate={(href) => {
            navigate(href);
            setIsMoreOpen(false);
          }}
        />
      )}
    </>
  );
}

// ─── More bottom sheet ───────────────────────────────────────────────────────────
type MoreBottomSheetProps = {
  links: NavLink[];
  onClose: () => void;
  onNavigate: (href: string) => void;
};

function MoreBottomSheet({ links, onClose, onNavigate }: MoreBottomSheetProps) {
  const location = useLocation();
  const activePath = location.pathname;

  const isActive = (href?: string) =>
    href === activePath || (href && activePath.startsWith(href + "/"));

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 inset-x-0 bg-[var(--color-bg-surface)] rounded-t-2xl z-50 md:hidden pb-safe shadow-[0_-4px_24px_rgba(0,0,0,0.12)]">
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[var(--color-bg-muted)]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2">
          <span className="text-base font-medium text-[var(--color-text-primary)]">
            More
          </span>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--color-bg-muted)] transition-colors text-[var(--color-text-secondary)]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Links */}
        <nav className="px-3 pb-6 space-y-0.5 max-h-[60vh] overflow-y-auto">
          {links.map((link) => {
            const Icon = link.icon;
            const active = isActive(link.href);

            // Section divider
            if (link.sectionLabel !== undefined) {
              return (
                <div
                  key={`section-${link.label}`}
                  className="pt-3 pb-1 px-3 first:pt-0"
                >
                  <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-text-tertiary)]">
                    {link.sectionLabel}
                  </span>
                </div>
              );
            }

            return (
              <button
                key={link.label}
                onClick={() => link.href && onNavigate(link.href)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors text-left ${
                  active
                    ? "bg-[var(--color-info-50)] text-[var(--color-info-700)]"
                    : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)]"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">{link.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}

export default BottomTabBar;
